import React, { useState, useEffect, useMemo, useRef, useCallback, memo, forwardRef } from 'react'
import ReactDOM from 'react-dom'
import { concatStyles, getLabel } from 'src/utils'
import { LabelOption } from 'src/@types'
import { useStyles } from './styles'

interface SelectInputProps {
  label: string
  value: string | number
  options: LabelOption[]
  selectBy: string
  showBy: string[] | any
  placeholder?: string
  onChange: Function
  error?: string
  className?: string
  disabled?: boolean
  id?: string
  onLoad?: Function
}

const SelectInputComponent = forwardRef<HTMLInputElement, SelectInputProps>(
  ({ options = [], value, label, selectBy, showBy, placeholder, onChange, error, className, onLoad, ...rest }, ref) => {
    const [inputValue, setInputValue] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const [focused, setFocused] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

    const list = useMemo(() => options, [options])
    const listRef = useRef<HTMLUListElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLUListElement | null>(null)
    const itemRefs = useRef<(HTMLLIElement | null)[]>([])
    const classes = useStyles()

    // Callback ref para atribuir a ambos os refs
    const setDropdownRef = useCallback((el: HTMLUListElement | null) => {
      ;(listRef as React.MutableRefObject<HTMLUListElement | null>).current = el
      dropdownRef.current = el
    }, [])

    // =========================
    // FILTER
    // =========================
    const filtered = useMemo(() => {
      const selectedItem = list.find((item) => item[selectBy] === value)

      // 👉 não está digitando → lista completa sem selecionado
      if (!searchValue.trim()) {
        return list.filter((item) => item[selectBy] !== selectedItem?.[selectBy])
      }

      // 👉 digitando → filtra
      return list.filter((item) => getLabel(item, showBy).toLowerCase().includes(searchValue.toLowerCase()))
    }, [searchValue, list, value])

    // =========================
    // POSITION
    // =========================
    const updatePosition = useCallback(() => {
      if (!wrapperRef.current) return

      const rect = wrapperRef.current.getBoundingClientRect()

      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom - 6,
        left: rect.left,
        width: rect.width,
        zIndex: 1600
      })
    }, [])

    useEffect(() => {
      if (focused) updatePosition()
    }, [focused])

    useEffect(() => {
      if (!focused) return

      const onScrollOrResize = () => updatePosition()

      window.addEventListener('resize', onScrollOrResize)
      document.addEventListener('scroll', onScrollOrResize, true)

      return () => {
        window.removeEventListener('resize', onScrollOrResize)
        document.removeEventListener('scroll', onScrollOrResize, true)
      }
    }, [focused])

    // =========================
    // CLICK OUTSIDE
    // =========================
    useEffect(() => {
      const handleClickOutside = (e: any) => {
        const isOutsideWrapper = wrapperRef.current && !wrapperRef.current.contains(e.target)
        const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(e.target)

        if (isOutsideWrapper && isOutsideDropdown) {
          setFocused(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('touchstart', handleClickOutside)
      }
    }, [])

    // =========================
    // VALUE SYNC
    // =========================
    useEffect(() => {
      const selectedItem = list.find((item) => item[selectBy] === value)

      if (selectedItem) {
        const label = getLabel(selectedItem, showBy)
        setInputValue(label)
        setSearchValue('')
        onLoad?.(selectedItem)
      } else {
        setInputValue('')
        setSearchValue('')
      }
    }, [value, list])

    // =========================
    // SCROLL SETAS
    // =========================
    useEffect(() => {
      if (highlightedIndex < 0) return

      const el = itemRefs.current[highlightedIndex]
      const container = listRef.current

      if (el && container) {
        const elTop = el.offsetTop
        const elBottom = elTop + el.offsetHeight

        const containerTop = container.scrollTop
        const containerBottom = containerTop + container.clientHeight

        if (elTop < containerTop) {
          container.scrollTop = elTop
        } else if (elBottom > containerBottom) {
          container.scrollTop = elBottom - container.clientHeight
        }
      }
    }, [highlightedIndex])

    // =========================
    // HANDLERS
    // =========================
    const handleChangeInput = (value: string) => {
      setInputValue(value)
      setSearchValue(value)
      setHighlightedIndex(-1)

      if (!focused) setFocused(true)
    }

    const handleClick = (option: LabelOption) => {
      const label = getLabel(option, showBy)

      setInputValue(label)
      setSearchValue('')
      onChange(option)
      setFocused(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prev) => Math.min(prev + 1, filtered.length - 1))
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prev) => Math.max(prev - 1, 0))
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        if (highlightedIndex >= 0) {
          handleClick(filtered[highlightedIndex])
        }
      }
    }

    // =========================
    // RENDER
    // =========================
    return (
      <div className={concatStyles(classes.filterButtonWrapper, className)}>
        <label className='select-input__label'>{label}</label>

        <div ref={wrapperRef}>
          <input
            ref={ref}
            value={inputValue}
            placeholder={placeholder}
            className={`select-input__input ${error ? 'select-input__input--error' : ''}`}
            onClick={() => {
              setFocused(true)
              setHighlightedIndex(-1)
              setSearchValue('') // 👈 abre sem filtrar
            }}
            onBlur={() => {
              setFocused(false)
              setSearchValue('')

              const match = list.find((item) => getLabel(item, showBy).toLowerCase() === inputValue.toLowerCase())

              if (!match) {
                setInputValue('')
                onChange(null)
              } else {
                onChange(match)
              }
            }}
            onChange={(e) => handleChangeInput(e.target.value)}
            onKeyDown={handleKeyDown}
            {...rest}
          />

          {error && <span className='select-input__error'>{error}</span>}

          {focused &&
            ReactDOM.createPortal(
              <ul ref={setDropdownRef} className={classes.selectInputDropper} style={dropdownStyle}>
                {filtered.length > 0 ? (
                  filtered.map((item, index) => (
                    <li
                      key={item[selectBy]}
                      ref={(el) => (itemRefs.current[index] = el)}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleClick(item)
                      }}
                      onTouchStart={(e) => {
                        const touch = e.touches[0]
                        e.currentTarget.dataset.touchStartY = String(touch.clientY)
                      }}
                      onTouchEnd={(e) => {
                        const touch = e.changedTouches[0]
                        const startY = Number(e.currentTarget.dataset.touchStartY || 0)

                        if (Math.abs(touch.clientY - startY) < 10) {
                          handleClick(item)
                        }
                      }}
                      className={highlightedIndex === index ? 'highlighted' : ''}
                    >
                      {getLabel(item, showBy)}
                    </li>
                  ))
                ) : (
                  <li>Nenhum registro encontrado.</li>
                )}
              </ul>,
              document.body
            )}
        </div>
      </div>
    )
  }
)

SelectInputComponent.displayName = 'SelectInputComponent'
export const SelectInput = memo(SelectInputComponent)
